import React, { useMemo } from "react";
import {
  FIELD_TYPES,
  VISIT_REQUIRED_FIELDS,
  VISIT_COUNTRY_COLUMNS_WIDTH,
  VISIT_STUDY_COLUMNS_WIDTH,
} from "../constants";
import { getCommentType, getCommentWidth } from "./table.utils";

import { Typography } from "@material-ui/core";
import styled from "styled-components";
import OutlinedButton from "../../../../app/components/buttons/outlined";

export default function TableHeader({
  value,
  visitToUse,
  fieldType,
  setEditOpen,
  setOverheadPct,
  isCountry,
  readOnly,
}) {
  const commentType = useMemo(() => getCommentType(fieldType), [fieldType]);

  const commentWidth = useMemo(() => getCommentWidth(commentType, readOnly), [commentType, readOnly]);

  // Get column widths based on context (country vs study, expanded vs collapsed)
  const widths = useMemo(() => {
    const config = isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH : VISIT_STUDY_COLUMNS_WIDTH;
    const mode = value === 0 ? 'expanded' : 'collapsed';
    
    return {
      visitCategory: config.visitCategory[mode],
      visitName: config.visitName[mode],
      studyDay: config.studyDay[mode],
      variance: config.variance[mode],
      visitWindow: config.visitWindow[mode],
      overheadPct: isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH.overheadPct[mode] : null,
      overhead: isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH.overhead[mode] : null,
      totalVisitCost: isCountry ? VISIT_COUNTRY_COLUMNS_WIDTH.totalVisitCost[mode] : null,
      deleteButton: config.deleteButton,
    };
  }, [isCountry, value]);

  return (
    <>
      <Row variant="header">
        <Cell
          width={widths.visitCategory}
          variant="header"
        >
          <Typography variant="body1">
            <strong>
              Visit Category{" "}
              {VISIT_REQUIRED_FIELDS["visitCategory"] && (
                <RequiredContainer>*</RequiredContainer>
              )}
            </strong>
          </Typography>
        </Cell>
        <Cell
          width={widths.visitName}
          variant="header"
        >
          <Typography variant="body1">
            <strong>
              Visit Name{" "}
              {VISIT_REQUIRED_FIELDS["visitName"] && (
                <RequiredContainer>*</RequiredContainer>
              )}
            </strong>
          </Typography>
        </Cell>
        <Cell
          width={widths.studyDay}
          variant="header"
        >
          <Typography variant="body1">
            <strong>
              Study {visitToUse?.visitIntervalType || "Day"}{" "}
              {VISIT_REQUIRED_FIELDS["visitStudyDay"] && (
                <RequiredContainer>*</RequiredContainer>
              )}
            </strong>
          </Typography>
        </Cell>
        <Cell
          width={widths.variance}
          variant="header"
        >
          <Typography variant="body1">
            <strong>(+/-)</strong>
          </Typography>
        </Cell>
        <Cell
          width={widths.visitWindow}
          variant="header"
        >
          <Typography variant="body1">
            <strong>
              Visit Window {visitToUse?.visitWindowIntervalType || "Day"}
            </strong>
          </Typography>
        </Cell>

        {(fieldType === FIELD_TYPES.COST || value !== 0) && isCountry && (
          <>
            <Cell
              width={widths.overheadPct}
              variant="header"
            >
              <HeaderContentContainer>
                <Typography variant="body1">
                  <strong>
                    Overhead %{" "}
                    {VISIT_REQUIRED_FIELDS["visitCost"] && (
                      <RequiredContainer>*</RequiredContainer>
                    )}
                  </strong>
                </Typography>
                {!readOnly && (<OutlinedButton
                  style={{
                    marginTop: "5px",
                    fontSize: "0.65rem",
                    padding: "2px 8px",
                    minWidth: "30px",
                    fontWeight: "bold",
                  }}
                  onClick={() => {
                    if (setOverheadPct) {
                      setOverheadPct();
                    }
                  }}
                  title={`"Apply To All" will apply the first value found to the rest of the cells in the column.`}
                >
                  Apply To All
                </OutlinedButton>)}
              </HeaderContentContainer>
            </Cell>
            <Cell
              width={widths.overhead}
              variant="header"
            >
              <Typography variant="body1">
                <strong>Overhead </strong>
              </Typography>
            </Cell>
            <Cell
              width={widths.totalVisitCost}
              variant="header"
            >
              <Typography variant="body1">
                <strong>Total Visit Cost</strong>
              </Typography>
            </Cell>
          </>
        )}
        {value === 0 && (
          <>
            {fieldType === FIELD_TYPES.MEDICAL && (
              <>
                <Cell
                  width="6%"
                  variant="header"
                >
                  <Typography variant="body1">
                    <strong>100% SDR/SDV Time (mins)</strong>
                  </Typography>
                </Cell>
                <Cell
                  width="6%"
                  variant="header"
                >
                  <Typography variant="body1">
                    <strong>CRA Sampled Visit</strong>
                  </Typography>
                </Cell>
                <Cell
                  width="6%"
                  variant="header"
                >
                  <Typography variant="body1">
                    <strong>Onsite SDR/SDV (mins)</strong>
                  </Typography>
                </Cell>
                <Cell
                  width="6%"
                  variant="header"
                >
                  <Typography variant="body1">
                    <strong>
                      CDA Review (mins){" "}
                      {!readOnly && (<EditButton onClick={() => setEditOpen(true)}>
                        {visitToUse.CDAReviewPctTime}
                      </EditButton>)}
                    </strong>
                  </Typography>
                </Cell>
              </>
            )}

            {fieldType === FIELD_TYPES.DATA_MANAGEMENT && (
              <>
                <Cell
                  width="6%"
                  variant="header"
                >
                  <Typography variant="body1">
                    <strong>Unique CRFs</strong>
                  </Typography>
                </Cell>
                <Cell
                  width="6%"
                  variant="header"
                >
                  <Typography variant="body1">
                    <strong>Non-unique CRFs</strong>
                  </Typography>
                </Cell>
              </>
            )}
            <Cell
              width={commentWidth}
              variant="header"
            >
              <Typography variant="body1">
                <strong>{commentType}</strong>
              </Typography>
            </Cell>
          </>
        )}

        {(!readOnly && value === 0) && (
          <Cell
            width={widths.deleteButton}
            variant="header"
          ></Cell>
        )}
      </Row>
    </>
  );
}

const Row = styled.div`
  display: flex;
  align-items: stretch;
  min-height: clamp(60px, auto, 100px);
  width: 100%;
  background: var(--surface-100);
  border-bottom: ${(p) => p.noBorder ? "none" : "1px solid var(--surface-300)"};
  position: ${(p) => (p.variant === "header" ? "sticky" : "relative")};
  top: ${(p) => (p.variant === "header" ? "0" : "auto")};
  z-index: ${(p) => (p.variant === "header" ? "10" : "1")};
  
  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${(p) => (typeof p.width === "function" ? p.width() : p.width)};
  padding: 8px 5px;
  text-align: center;
  background: ${(p) => p.variant === "header" ? "var(--surface-100)" : "var(--White)"};
  border-right: 1px solid var(--surface-300);
  border-left: ${(p) => p.leftBorder ? "1px solid var(--surface-300)" : "none"};
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  &:last-child {
    border-right: ${(p) => p.rightBorder ? "1px solid var(--surface-300)" : "none"};
  }
  
  > * {
    text-align: center;
    width: 100%;
  }
`;

const HeaderContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 100%;
`;

const RequiredContainer = styled.span`
  color: red;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--teal-700);
  text-decoration: underline;
`;
